import { Badge } from "@/components/ui/badge";
import { isPendingEnrollment, isEndingEnrollment, isStillEffectiveEnding } from "@/lib/enrollmentFilters";
import { formatDateDisplay } from "@/lib/utils";

interface EnrollmentBadgesProps {
  enrollment: {
    status?: string | null;
    effectiveDate?: string | Date | null;
    endDate?: string | Date | null;
    waivedCoverage?: boolean | null;
    enrollmentProgress?: string | null;
  };
  className?: string;
  showProgress?: boolean;
}

const PROGRESS_BADGE_STYLES: Record<string, string> = {
  not_started: "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300",
  viewed: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-300",
  in_progress: "bg-yellow-50 text-yellow-700 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-300",
  submitted: "bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-900 dark:text-purple-300",
  under_review: "bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-900 dark:text-orange-300",
  confirmed: "bg-green-50 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-300",
  declined: "bg-red-50 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-300",
};

function formatProgressLabel(progress: string): string {
  return progress.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Unified component for displaying enrollment status badges
 * Shows appropriate badges based on enrollment state (Pending, Active, Ending)
 * When showProgress is true, also displays the enrollment workflow progress
 */
export function EnrollmentBadges({ enrollment, className, showProgress = false }: EnrollmentBadgesProps) {
  const isPending = isPendingEnrollment(enrollment);
  const isEnding = isEndingEnrollment(enrollment);
  const progress = enrollment.enrollmentProgress;
  const isWaived = Boolean(enrollment.waivedCoverage) || enrollment.status === 'waived';

  // Future endDate = coverage still active now, just scheduled to end. Covers both
  // a plan switch (terminated) AND a future-dated waiver: show Active + Terminating
  // until the endDate, then fall through to Waived/Terminated afterwards.
  const stillEffectiveEnding = isStillEffectiveEnding(enrollment);

  return (
    <div className={`flex flex-wrap gap-1.5 ${className || ''}`}>
      {isPending ? (
        <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200">
          Pending - Starts {enrollment.effectiveDate ? formatDateDisplay(String(enrollment.effectiveDate)) : '-'}
        </Badge>
      ) : stillEffectiveEnding ? (
        <>
          <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            Active
          </Badge>
          <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900 dark:text-orange-200">
            Terminating {formatDateDisplay(String(enrollment.endDate!))}
          </Badge>
        </>
      ) : isWaived ? (
        <Badge variant="outline" className="bg-stone-100 text-stone-700 border-stone-300 dark:bg-stone-800 dark:text-stone-300">
          Waived
        </Badge>
      ) : enrollment.status === 'terminated' ? (
        <Badge variant="destructive" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
          Terminated
        </Badge>
      ) : (
        <>
          <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {enrollment.status === 'active' ? 'Active' : enrollment.status || 'Active'}
          </Badge>
          {isEnding && !isPending && enrollment.endDate && (
            <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900 dark:text-orange-200">
              Terminating {formatDateDisplay(String(enrollment.endDate))}
            </Badge>
          )}
        </>
      )}
      
      {showProgress && (
        <Badge variant="outline" className={PROGRESS_BADGE_STYLES[progress || 'not_started'] || PROGRESS_BADGE_STYLES.not_started}>
          {formatProgressLabel(progress || 'not_started')}
        </Badge>
      )}
    </div>
  );
}
